import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  Stack,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  LinearProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Check as CheckIcon,
  Star as StarIcon,
  TrendingDown as TrendingDownIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  EmojiObjects as EmojiObjectsIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { useAgentReport } from '../../../../contexts/AgentReportContext';

const AIInsights = ({ agentId }) => {
  const theme = useTheme();
  const { fetchSingleAgentAIInsights } = useAgentReport();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiData, setAiData] = useState(null);

  // Fetch AI insights data
  useEffect(() => {
    const fetchAIInsightsData = async () => {
      if (!agentId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const data = await fetchSingleAgentAIInsights(agentId);
        setAiData(data);
        setLoading(false);
      } catch (err) {
        setError(err);
        console.error('Error fetching AI insights:', err);
        setLoading(false);
      }
    };

    fetchAIInsightsData();
  }, [agentId, fetchSingleAgentAIInsights]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography color="error">Error loading AI insights: {error.message}</Typography>
      </Box>
    );
  }

  // No data state
  if (!aiData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>No AI insights available</Typography>
      </Box>
    );
  }

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Header Summary */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmojiObjectsIcon sx={{ color: theme.palette.primary.main }} />
              AI-Powered Performance Analysis
            </Typography>
            <Chip 
              label={`Overall Score: ${aiData.summary?.overall_ai_score}/10`}
              color="primary"
              sx={{ fontWeight: 600, fontSize: '1rem', py: 2.5 }}
            />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {aiData.summary?.total_calls_analyzed || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Calls Analyzed</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  {Math.floor((aiData.summary?.avg_call_duration_sec || 0) / 60)}m {(aiData.summary?.avg_call_duration_sec || 0) % 60}s
                </Typography>
                <Typography variant="body2" color="text.secondary">Avg Call Duration</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Analysis Period
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {aiData.analysis_period?.from} to {aiData.analysis_period?.to}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Key Strengths */}
      <Grid item xs={12} lg={6}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ color: theme.palette.success.main }} />
            Key Strengths
          </Typography>
          <Stack spacing={2}>
            {aiData.key_strengths?.map((strength, index) => (
              <Accordion key={index} sx={{ boxShadow: 'none', border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {strength.title}
                      </Typography>
                      <Chip 
                        label={`${Math.round(strength.confidence_score * 100)}% Confidence`}
                        size="small"
                        color="success"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={strength.confidence_score * 100} 
                      sx={{ 
                        height: 6, 
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.success.main
                        }
                      }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Supporting Metrics:
                    </Typography>
                    {Object.entries(strength.supporting_metrics || {}).map(([key, value]) => (
                      <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                        • {key.replace(/_/g, ' ')}: <strong>{value}</strong>
                      </Typography>
                    ))}
                    {strength.examples && strength.examples.length > 0 && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Example:
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                          <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1 }}>
                            "{strength.examples[0].transcript_snippet}"
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Call ID: {strength.examples[0].call_id} • Timestamp: {strength.examples[0].timestamp_sec}s
                          </Typography>
                        </Paper>
                      </>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Paper>
      </Grid>

      {/* Areas for Improvement */}
      <Grid item xs={12} lg={6}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon sx={{ color: theme.palette.warning.main }} />
            Areas for Improvement
          </Typography>
          <Stack spacing={2}>
            {aiData.areas_for_improvement?.map((area, index) => (
              <Accordion key={index} sx={{ boxShadow: 'none', border: `1px solid ${alpha(theme.palette[getSeverityColor(area.severity)].main, 0.2)}` }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {area.title}
                      </Typography>
                      <Chip 
                        label={`${area.severity} Priority`}
                        size="small"
                        color={getSeverityColor(area.severity)}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Supporting Metrics:
                    </Typography>
                    {Object.entries(area.supporting_metrics || {}).map(([key, value]) => (
                      <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                        • {key.replace(/_/g, ' ')}: <strong>{value}</strong>
                      </Typography>
                    ))}
                    {area.examples && area.examples.length > 0 && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Example:
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                          <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1 }}>
                            "{area.examples[0].transcript_snippet}"
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Call ID: {area.examples[0].call_id} • Timestamp: {area.examples[0].timestamp_sec}s
                          </Typography>
                        </Paper>
                      </>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Paper>
      </Grid>

      {/* AI Coaching Insights */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon sx={{ color: theme.palette.info.main }} />
            AI Coaching Recommendations
          </Typography>
          <Grid container spacing={3}>
            {aiData.ai_coaching_insights?.map((insight, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ 
                  height: '100%',
                  p: 2.5,
                  border: `2px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease',
                    boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.2)}`,
                  }
                }}>
                  <Chip 
                    label={insight.insight_type?.replace(/_/g, ' ').toUpperCase()}
                    size="small"
                    color="info"
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {insight.message}
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Recommended Action:
                  </Typography>
                  <Paper sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {insight.recommended_action}
                    </Typography>
                  </Paper>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip 
                      label={`${Math.round(insight.confidence_score * 100)}% Confidence`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Grid>

      {/* Performance Scorecard */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Performance Scorecard
          </Typography>
          <Grid container spacing={3}>
            {aiData.scorecard && Object.entries(aiData.scorecard).map(([key, value]) => {
              if (key === 'professionalism_and_compliance') {
                return (
                  <Grid item xs={12} md={6} key={key}>
                    <Card sx={{ p: 2.5, height: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {key.replace(/_/g, ' ').toUpperCase()}
                        </Typography>
                        <Chip 
                          label={`${value.score}/10`}
                          color={value.score >= 7 ? 'success' : value.score >= 5 ? 'warning' : 'error'}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={(value.score / 10) * 100}
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          mb: 2,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        }}
                      />
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Script Adherence:
                      </Typography>
                      {value.metrics?.script_adherence && Object.entries(value.metrics.script_adherence).map(([k, v]) => (
                        <Typography key={k} variant="body2" sx={{ mb: 0.5, ml: 1 }}>
                          • {k.replace(/_/g, ' ')}: <strong>{Math.round(v * 100)}%</strong>
                        </Typography>
                      ))}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 1.5 }}>
                        Language & Tone Quality:
                      </Typography>
                      {value.metrics?.language_and_tone_quality && Object.entries(value.metrics.language_and_tone_quality).map(([k, v]) => (
                        <Typography key={k} variant="body2" sx={{ mb: 0.5, ml: 1 }}>
                          • {k.replace(/_/g, ' ')}: <strong>{Math.round(v * 100)}%</strong>
                        </Typography>
                      ))}
                    </Card>
                  </Grid>
                );
              }
              return (
                <Grid item xs={12} md={6} lg={3} key={key}>
                  <Card sx={{ p: 2.5, height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        {key.replace(/_/g, ' ').toUpperCase()}
                      </Typography>
                      <Chip 
                        label={`${value.score}/10`}
                        size="small"
                        color={value.score >= 7 ? 'success' : value.score >= 5 ? 'warning' : 'error'}
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(value.score / 10) * 100}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        mb: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    />
                    {value.metrics && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Metrics:
                        </Typography>
                        {Object.entries(value.metrics).map(([k, v]) => (
                          <Typography key={k} variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                            • {k.replace(/_/g, ' ')}: <strong>{Array.isArray(v) ? v.join(', ') : v}</strong>
                          </Typography>
                        ))}
                      </>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default AIInsights;
